import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig } from '../shared/FormComponents';
import SSECreationProgress from '../shared/SSECreationProgress';
import { Collaborator } from '../shared/CollaboratorSelector';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, createTableFormTourSteps } from '../shared/FeatureTour';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import FieldShell, { FOCUS_RING, SELECTED_TINT, FIELD_LABEL_CLASS, FIELD_HELP_CLASS } from '../shared/TailwindComponents/FieldShell';
import Toggle from '../shared/TailwindComponents/Toggle';
import apis from '../../utils/apis';
import { PlaybookDetail, ClauseRule } from '../../types/playbook';
import { showModal } from '../../store/actions/modalActions';
import {
  PiPlusLight,
  PiTrashLight,
  PiTableLight,
  PiColumnsLight,
  PiShieldCheckLight,
  PiMagnifyingGlassLight,
  PiSpinnerGapLight,
} from 'react-icons/pi';
import {
  createBatchTableSSE,
  resetBatchTableCreation,
  fetchTemplates,
} from '../../store/actions/BatchTables/BatchTablesActions';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import { fetchLinkableProjects } from '../../store/actions/Projects/ProjectActions';
import { TableTemplate, CreateTablePayload, ColumnDataType } from '../../types/batchTables';
import { PlaybookListItem } from '../../types/playbook';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import Spinner from '../shared/TailwindComponents/Spinner';
import { MAX_UPLOAD_BYTES } from '../../utils/uploadLimits';

// ─── Picker trigger (template / playbook) ───────────────────────────────────

/**
 * Both pickers are the same control - a full-width button that opens a modal —
 * so they share one surface recipe instead of drifting apart. It follows the
 * field-state matrix (`fieldSurface`) rather than repeating it: a trigger is not
 * a text field, so it takes the resting fill, the hairline and the shared focus
 * ring, and shows its "chosen" state as a champagne tint.
 */
const PICKER_TRIGGER = clsx(
  'group flex w-full items-center justify-between rounded-md border px-4 py-2.5 text-start',
  'transition-colors duration-200 ease-slide',
  FOCUS_RING
);

const PICKER_STATE = (selected: boolean, disabled?: boolean) =>
  disabled
    ? 'border-hairline-soft bg-surface-sunken/40 cursor-not-allowed'
    : selected
      ? SELECTED_TINT
      : 'border-hairline bg-field hover:bg-field-hover';

// ─── Column Editor (custom BaseForm field) ──────────────────────────────────

interface ColumnItem {
  name: string;
  query: string;
  data_type: ColumnDataType;
  clause_uuid?: string;
}

const dataTypeOptions: DropdownOption[] = [
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'currency', label: 'Currency' },
];

interface ColumnsEditorProps {
  id?: string;
  name?: string;
  label?: string;
  value: ColumnItem[];
  onChange: (value: ColumnItem[]) => void;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  templateName?: string;
  templateColumns?: ColumnItem[];
  isLoading?: boolean;
}

const ColumnsEditor: React.FC<ColumnsEditorProps> = ({
  value = [],
  onChange,
  disabled,
  readOnly,
  templateName,
  templateColumns: tmplCols,
  isLoading,
}) => {
  const { t } = useTranslation();
  const lastTemplateRef = useRef<string | undefined>(undefined);

  // Auto-seed columns when a template is selected
  useEffect(() => {
    if (tmplCols && tmplCols.length > 0 && templateName !== lastTemplateRef.current) {
      lastTemplateRef.current = templateName;
      onChange(tmplCols);
    }
  }, [tmplCols, templateName]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = value || [];
  const PAGE_SIZE = 10;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    // Reset paging when a new template/playbook seeds columns
    setVisibleCount(PAGE_SIZE);
  }, [templateName]);

  const visibleColumns = columns.slice(0, visibleCount);
  const hiddenCount = Math.max(0, columns.length - visibleCount);

  const handleColumnChange = (index: number, field: keyof ColumnItem, val: string) => {
    const updated = [...columns];
    updated[index] = { ...updated[index], [field]: val };
    onChange(updated);
  };

  const addColumn = () => {
    onChange([...columns, { name: '', query: '', data_type: 'text' as ColumnDataType }]);
  };

  const removeColumn = (index: number) => {
    const updated = [...columns];
    updated.splice(index, 1);
    onChange(updated);
  };

  return (
    <div>
      {/* A FIELD label, not a heading: the section above already carries the
          one heading size, and a second `text-base` title under it read as a
          competing header of a different weight. */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className={FIELD_LABEL_CLASS}>
            {t('Columns')}
          </span>
          <p className={clsx('mt-0.5', FIELD_HELP_CLASS)}>
            {readOnly && templateName
              ? t(`Loaded from template "${templateName}". You can customize these columns or add more.`, { name: templateName })
              : t('Define the columns (queries) that will be applied to each document.')}
          </p>
        </div>
      </div>

      {isLoading && (
        <div className="rounded-md border border-dashed border-hairline py-12 text-center">
          <Spinner size="glyph" className="!h-8 !w-8 mx-auto mb-3" />
          <p className="text-sm text-fg-muted">
            {t('Loading playbook clauses...')}
          </p>
        </div>
      )}

      {!isLoading && columns.length === 0 && (
        <div className="rounded-md border border-dashed border-hairline py-12 text-center">
          <PiTableLight className="mx-auto mb-3 h-10 w-10 text-fg-muted" />
          <p className="text-sm text-fg-muted">
            {t('No columns defined. Add your first column or select a template above.')}
          </p>
          <Button
            type="button"
            variant="neutral"
            size="small"
            onClick={addColumn}
            className="mt-3 mx-auto"
          >
            <PiPlusLight className="w-4 h-4 me-1" />
            {t('Add column')}
          </Button>
        </div>
      )}

      <div className="space-y-3" style={{ display: isLoading ? 'none' : undefined }}>
        {visibleColumns.map((col, index) => (
          /*
            FLOATING COLUMN CARDS. Each column is a grey card lifted off the
            form body; `surface-canvas` turns the field token white inside it,
            so the inputs read white-on-grey instead of grey on a grey well.
            The card sets its own fill (not `bg-field`), so it stays grey.
          */
          <div
            key={index}
            className="surface-canvas rounded-lg border border-hairline-soft bg-surface-sunken/70 p-4 shadow-sm"
          >
            {/* Row 1: Column number + Name + Type + Remove */}
            <div className="flex items-start gap-3">
              <div className="flex items-center pt-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full border border-hairline-soft bg-surface text-xs font-medium text-fg-muted tabular-nums">
                  {index + 1}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <Input
                  id={`col-name-${index}`}
                  type="text"
                  label={t('Column name')}
                  placeholder={t('e.g., Effective Date')}
                  value={col.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleColumnChange(index, 'name', e.target.value)
                  }
                  disabled={disabled}
                />
              </div>
              <div className="flex-shrink-0">
                <Dropdown
                  id={`col-type-${index}`}
                  label={t('Type')}
                  options={dataTypeOptions}
                  value={col.data_type}
                  onChange={(val) =>
                    handleColumnChange(index, 'data_type', Array.isArray(val) ? val[0] : val)
                  }
                />
              </div>
              {!disabled && (
                <div className="flex-shrink-0 pt-6">
                  <button
                    type="button"
                    onClick={() => removeColumn(index)}
                    className={clsx(
                      'rounded-md p-2 text-fg-muted transition-colors duration-200 ease-slide',
                      'hover:bg-danger-50 hover:text-danger-500 dark:hover:bg-danger-900/20',
                      FOCUS_RING
                    )}
                    title={t('Remove column')}
                  >
                    <PiTrashLight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Row 2: Query (full width) */}
            <div className="mt-2 ps-9">
              <Input
                id={`col-query-${index}`}
                type="text"
                label={t('AI query')}
                placeholder={t('e.g., What is the effective date of the agreement?')}
                value={col.query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleColumnChange(index, 'query', e.target.value)
                }
                disabled={disabled}
              />
            </div>
          </div>
        ))}
      </div>

      {!isLoading && hiddenCount > 0 && (
        <div className="mt-3 flex justify-center">
          <Button
            type="button"
            variant="neutral"
            size="small"
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
          >
            {t('Load more')} ({hiddenCount})
          </Button>
        </div>
      )}

      {/* Footer: add column + count */}
      {!isLoading && columns.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            <Badge color="gray">{columns.length}</Badge>
            <span>{t('column' + (columns.length !== 1 ? 's' : '') + ' defined')}</span>
          </div>
          {!disabled && (
            <Button
              type="button"
              variant="neutral"
              size="small"
              onClick={addColumn}
            >
              <PiPlusLight className="w-4 h-4 me-1" />
              {t('Add column')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Template Selector (opens modal picker) ────────────────────────────────

interface TemplateSelectorProps {
  id?: string;
  name?: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  templates: TableTemplate[];
  selectedTemplate: TableTemplate | null;
  onTemplateChange: (templateId: string) => void;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  value,
  onChange,
  disabled,
  templates,
  selectedTemplate,
  onTemplateChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const handleSelect = (templateId: string) => {
    onChange(templateId);
    onTemplateChange(templateId);
  };

  // Shared picker via GlobalModal - the playbook selector below does the same.
  // Rendering it here instead put a `fixed` overlay inside a `.glass` card,
  // whose backdrop-filter traps fixed children: it was clipped to the card and
  // nothing in it could be clicked.
  const openPicker = () => {
    dispatch(
      showModal({
        showModal: true,
        type: 'templatePicker',
        message: '',
        templates,
        selectedTemplateId: value || '',
        onTemplateSelect: handleSelect,
      })
    );
  };

  return (
    <>
      <FieldShell label={t('Template')} translate={false}>
        <button
          type="button"
          disabled={disabled}
          onClick={openPicker}
          className={clsx(PICKER_TRIGGER, PICKER_STATE(!!selectedTemplate, disabled))}
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <PiColumnsLight className="h-4 w-4 flex-shrink-0 text-fg-muted" />
            {selectedTemplate ? (
              <div className="min-w-0">
                <span className="block truncate text-sm font-medium text-fg-strong">
                  {selectedTemplate.name}
                </span>
                <span className="text-xs text-fg-muted">
                  {selectedTemplate.column_count} {t('columns')}
                </span>
              </div>
            ) : (
              <span className="text-sm text-fg-muted">{t('Browse templates...')}</span>
            )}
          </div>
          <span className="flex-shrink-0 text-xs text-fg-muted opacity-0 transition-opacity group-hover:opacity-100">
            {selectedTemplate ? t('Change') : t('Browse')}
          </span>
        </button>
      </FieldShell>
    </>
  );
};

// ─── Playbook Selector (opens PlaybookPickerModal) ─────────────────────────

interface PlaybookSelectorProps {
  id?: string;
  name?: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  selectedPlaybook: PlaybookListItem | null;
  onPlaybookChange: (playbook: PlaybookListItem | null) => void;
}

const PlaybookSelector: React.FC<PlaybookSelectorProps> = ({
  value,
  onChange,
  disabled,
  error,
  selectedPlaybook,
  onPlaybookChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const handleSelect = (playbook: PlaybookListItem | null) => {
    onChange(playbook?.uuid || '');
    onPlaybookChange(playbook);
  };

  // Shared picker via GlobalModal - same modal the review form opens.
  const openPicker = () => {
    dispatch(
      showModal({
        showModal: true,
        type: 'playbookPicker',
        message: '',
        selectedPlaybookUuid: value || '',
        onPlaybookSelect: handleSelect,
      })
    );
  };

  return (
    <>
      <FieldShell
        label={t('Compliance playbook')}
        required
        translate={false}
        error={error}
        helpText={
          selectedPlaybook
            ? t('Compliance columns will be auto-generated from this playbook\'s {{count}} clause rules.', { count: selectedPlaybook.clause_count })
            : undefined
        }
      >
        <button
          type="button"
          disabled={disabled}
          onClick={openPicker}
          className={clsx(
            PICKER_TRIGGER,
            PICKER_STATE(!!selectedPlaybook, disabled),
            !selectedPlaybook && error && 'border-danger-500 ring-1 ring-danger-500/30'
          )}
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <PiShieldCheckLight className="h-4 w-4 flex-shrink-0 text-fg-muted" />
            {selectedPlaybook ? (
              <div className="min-w-0">
                <span className="block truncate text-sm font-medium text-fg-strong">
                  {selectedPlaybook.name}
                </span>
                <span className="text-xs text-fg-muted">
                  {selectedPlaybook.clause_count} {t('clauses')}
                </span>
              </div>
            ) : (
              <span className="text-sm text-fg-muted">{t('Browse playbooks...')}</span>
            )}
          </div>
          <span className="flex-shrink-0 text-xs text-fg-muted opacity-0 transition-opacity group-hover:opacity-100">
            {selectedPlaybook ? t('Change') : t('Browse')}
          </span>
        </button>
      </FieldShell>
    </>
  );
};

// ─── Mode Selector ───────────────────────────────────────────────────────────

type TableMode = 'extraction' | 'compliance';

interface ModeSelectorProps {
  mode: TableMode;
  onModeChange: (mode: TableMode) => void;
  disabled?: boolean;
}

const ModeSelector: React.FC<ModeSelectorProps> = ({ mode, onModeChange, disabled }) => {
  const { t } = useTranslation();

  const segment = (active: boolean) =>
    clsx(
      'flex flex-1 items-center gap-2 px-4 py-3 text-sm font-medium',
      'transition-colors duration-200 ease-slide',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-champagne/20',
      active
        ? 'bg-champagne/10 text-fg-strong'
        : 'bg-field text-fg-muted hover:bg-field-hover'
    );

  return (
    <FieldShell label={t('Table type')} translate={false}>
      <div
        role="radiogroup"
        className="flex w-full divide-x divide-hairline overflow-hidden rounded-md border border-hairline"
      >
        <button
          type="button"
          role="radio"
          aria-checked={mode === 'extraction'}
          disabled={disabled}
          onClick={() => onModeChange('extraction')}
          className={segment(mode === 'extraction')}
        >
          <PiMagnifyingGlassLight className="h-4 w-4 flex-shrink-0" />
          <div className="text-start">
            <div className="font-medium">{t('Extraction')}</div>
            <div className="text-xs font-normal text-fg-muted">{t('Extract data from documents')}</div>
          </div>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={mode === 'compliance'}
          disabled={disabled}
          onClick={() => onModeChange('compliance')}
          className={segment(mode === 'compliance')}
        >
          <PiShieldCheckLight className="h-4 w-4 flex-shrink-0" />
          <div className="text-start">
            <div className="font-medium">{t('Compliance')}</div>
            <div className="text-xs font-normal text-fg-muted">{t('Check against a playbook')}</div>
          </div>
        </button>
      </div>
    </FieldShell>
  );
};

// ─── Main Form ───────────────────────────────────────────────────────────────

interface CreateTableFormProps {
  onClose: (newUuid?: string) => void;
  matterId?: number;
  matterUuid?: string;
}

const CreateTableForm: React.FC<CreateTableFormProps> = ({ onClose, matterId, matterUuid }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const templates = useSelector((state: RootState) => state.batchTables.templates);
  const { isConnecting, connected, steps: sseSteps, complete, ok, summary } = useSelector(
    (state: RootState) => state.batchTables.sseCreate
  );

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [tableMode, setTableMode] = useState<TableMode>('extraction');
  const [selectedPlaybook, setSelectedPlaybook] = useState<PlaybookListItem | null>(null);
  const [selectedPlaybookUuid, setSelectedPlaybookUuid] = useState<string>('');
  const [inheritCollaborators, setInheritCollaborators] = useState<boolean>(true);
  const [playbookClauseColumns, setPlaybookClauseColumns] = useState<ColumnItem[]>([]);
  const [isLoadingPlaybookClauses, setIsLoadingPlaybookClauses] = useState(false);
  const lastFetchedPlaybookRef = useRef<string | null>(null);
  const [projectOptions, setProjectOptions] = useState<DropdownOption[]>([]);
  const userNavigatedAwayRef = useRef(false);
  const sseStartedAtRef = useRef<number | null>(null);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'create-table-form',
    steps: createTableFormTourSteps,
    readyToShow: !isLoadingData,
    autoStartDelay: 600,
  });

  // ─── Load data ──────────────────────────────────────────────────────────

  useEffect(() => {
    const loadData = async () => {
      setIsLoadingData(true);
      try {
        const [, , projectsResp] = await Promise.all([
          dispatch(fetchTemplates() as any),
          dispatch(fetchOrgPeople()),
          dispatch(fetchLinkableProjects() as any),
        ]);
        if (projectsResp && Array.isArray(projectsResp)) {
          setProjectOptions(projectsResp.map((p: any) => ({ value: p.uuid, label: p.title })));
        }
      } catch (error: any) {
        console.error('[CreateTableForm] Error loading data:', error?.message);
      } finally {
        setIsLoadingData(false);
      }
    };
    loadData();
  }, [dispatch]);

  // ─── Fetch playbook details when one is selected - seed clauses as columns ─
  useEffect(() => {
    if (!selectedPlaybookUuid) {
      setPlaybookClauseColumns([]);
      lastFetchedPlaybookRef.current = null;
      return;
    }
    if (lastFetchedPlaybookRef.current === selectedPlaybookUuid) return;
    let cancelled = false;
    setIsLoadingPlaybookClauses(true);
    (async () => {
      try {
        const response = await apis.get(`/playbooks/${selectedPlaybookUuid}`);
        if (cancelled || response.status !== 200) return;
        const detail: PlaybookDetail = response.data?.data ?? response.data;
        const clauses: ClauseRule[] = detail?.clause_rules || [];
        const seeded: ColumnItem[] = clauses
          .slice()
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          .map((c) => ({
            name: c.display_name || c.clause_type,
            query: c.description || c.starting_position || `Check the ${c.display_name || c.clause_type} clause.`,
            data_type: 'text' as ColumnDataType,
            clause_uuid: c.uuid,
          }));
        lastFetchedPlaybookRef.current = selectedPlaybookUuid;
        setPlaybookClauseColumns(seeded);
      } catch (error) {
        console.error('[CreateTableForm] Failed to fetch playbook detail:', error);
      } finally {
        if (!cancelled) setIsLoadingPlaybookClauses(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPlaybookUuid]);

  // ─── Template handling ──────────────────────────────────────────────────

  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return templates.find((t: TableTemplate) => String(t.id) === selectedTemplateId) || null;
  }, [templates, selectedTemplateId]);

  const templateColumns: ColumnItem[] = useMemo(() => {
    if (!selectedTemplate?.columns) return [];
    return selectedTemplate.columns.map((c) => ({
      name: c.name,
      query: c.query,
      data_type: c.data_type || 'text',
    }));
  }, [selectedTemplate]);

  // ─── Template change handler ─────────────────────────────────────────

  const handleTemplateChange = useCallback(
    (templateId: string) => {
      setSelectedTemplateId(templateId);
    },
    []
  );

  const handlePlaybookChange = useCallback((playbook: PlaybookListItem | null) => {
    setSelectedPlaybook(playbook);
    setSelectedPlaybookUuid(playbook?.uuid || '');
  }, []);

  const handleModeChange = useCallback((mode: TableMode) => {
    setTableMode(mode);
    // Clear mutually exclusive selection
    if (mode === 'compliance') {
      setSelectedTemplateId('');
    } else {
      setSelectedPlaybook(null);
      setSelectedPlaybookUuid('');
      setInheritCollaborators(true);
    }
  }, []);

  // ─── Allowed file extensions for tables ─────────────────────────────────

  const tableFileExtensions = useMemo(() => ['pdf', 'doc', 'docx', 'xlsx', 'xls', 'txt', 'csv'], []);

  // ─── Form sections ─────────────────────────────────────────────────────

  const formSections: BaseFormSectionConfig[] = useMemo(() => {
    const isTemplateSelected = !!selectedTemplateId;
    const isComplianceMode = tableMode === 'compliance';

    const columnsSection: BaseFormSectionConfig = isComplianceMode
      ? {
          title: t('Playbook'),
          description: t('Select a compliance playbook. Columns are auto-generated from its clause rules.'),
          fields: [
            {
              name: 'mode',
              type: 'custom',
              label: '',
              gridSize: 'full',
              customComponent: ModeSelector,
              customProps: {
                mode: tableMode,
                onModeChange: handleModeChange,
              },
            } as BaseFormFieldConfig,
            {
              name: 'playbook_uuid',
              type: 'custom',
              label: t('Compliance playbook'),
              gridSize: 'full',
              customComponent: PlaybookSelector,
              customProps: {
                selectedPlaybook,
                onPlaybookChange: handlePlaybookChange,
              },
            } as BaseFormFieldConfig,
            {
              name: 'inherit_collaborators',
              type: 'custom',
              label: t('Collaborator inheritance'),
              gridSize: 'full',
              customComponent: () => (
                <FieldShell label={t('Collaborator inheritance')} translate={false}>
                  <Toggle
                    enabled={inheritCollaborators}
                    onToggle={(next) => setInheritCollaborators(next)}
                    label={t('Auto-share deep dive reviews with table collaborators')}
                    translate={false}
                  />
                </FieldShell>
              ),
              customProps: {},
            } as BaseFormFieldConfig,
            {
              name: 'columns',
              type: 'custom',
              label: '',
              gridSize: 'full',
              customComponent: ColumnsEditor,
              customProps: {
                readOnly: false,
                templateName: selectedPlaybook?.uuid,
                templateColumns: playbookClauseColumns,
                isLoading: isLoadingPlaybookClauses,
              },
            } as BaseFormFieldConfig,
          ],
        }
      : {
          title: t('Columns'),
          description: t('Select a template or define custom columns to query each document.'),
          fields: [
            {
              name: 'mode',
              type: 'custom',
              label: '',
              gridSize: 'full',
              customComponent: ModeSelector,
              customProps: {
                mode: tableMode,
                onModeChange: handleModeChange,
              },
            } as BaseFormFieldConfig,
            {
              name: 'template_id',
              type: 'custom',
              label: t('Template'),
              gridSize: 'full',
              customComponent: TemplateSelector,
              customProps: {
                templates,
                selectedTemplate,
                onTemplateChange: handleTemplateChange,
              },
            } as BaseFormFieldConfig,
            {
              name: 'columns',
              type: 'custom',
              label: '',
              gridSize: 'full',
              customComponent: ColumnsEditor,
              customProps: {
                readOnly: false,
                templateName: isTemplateSelected ? selectedTemplate?.name : undefined,
                templateColumns: isTemplateSelected ? templateColumns : undefined,
              },
            } as BaseFormFieldConfig,
          ],
        };

    return [
      {
        dataTourId: 'create-table-basics',
        // Untitled: the form header ("New table") already names these fields.
        fields: [
          {
            name: 'tableName',
            type: 'input',
            label: t('Table name'),
            placeholder: t('e.g., Due Diligence Review — Project Alpha'),
            required: true,
            gridSize: 'full',
          } as BaseFormFieldConfig,
          {
            name: 'description',
            type: 'textarea',
            label: t('Description'),
            placeholder: t('Briefly describe what this table analyzes...'),
            rows: 2,
            gridSize: 'full',
          } as BaseFormFieldConfig,
          ...(projectOptions.length > 0 ? [{
            name: 'project_uuid',
            type: 'dropdown' as const,
            label: t('Link to project'),
            placeholder: t('Select a project'),
            required: false,
            customProps: {fullWidth: true} as any,
            gridSize: 'full' as const,
            options: projectOptions,
          } as BaseFormFieldConfig] : []),
        ],
      },
      {
        dataTourId: 'create-table-access',
        title: t('Access'),
        description: t('Grant access to people who should collaborate on this table.'),
        fields: [
          {
            name: 'collaborators',
            type: 'collaboratorSelector',
            // No field-level title - the section header ("Access") labels this.
            label: '',
            gridSize: 'full',
          } as BaseFormFieldConfig,
        ],
      },
      { ...columnsSection, dataTourId: 'create-table-columns' },
      {
        dataTourId: 'create-table-documents',
        title: t('Documents'),
        description: t('Upload the documents you want to analyze. Supported: PDF, Word, Excel, TXT, CSV.'),
        fields: [
          {
            name: 'files',
            type: 'fileUploader',
            label: t('Files'),
            required: true,
            gridSize: 'full',
            allowedExtensions: tableFileExtensions,
            maxFileSize: MAX_UPLOAD_BYTES,
          } as BaseFormFieldConfig,
        ],
      },
    ];
  }, [t, templates, selectedTemplateId, selectedTemplate, templateColumns, tableFileExtensions, handleTemplateChange, tableMode, handleModeChange, selectedPlaybook, handlePlaybookChange, inheritCollaborators, playbookClauseColumns, isLoadingPlaybookClauses, projectOptions]);

  // ─── Validation ─────────────────────────────────────────────────────────

  const schema = useMemo(() => {
    const isComplianceMode = tableMode === 'compliance';

    return new FormSchema('createTable', [
      new FormField('tableName', 'text', true, t('Table name')),
      new FormField('description', 'text', false, t('Description')),
      new FormField('template_id', 'text', false, t('Template')),
      new FormField('mode', 'text', false, t('Mode')),
      new FormField('playbook_uuid', 'text', isComplianceMode, t('Playbook'), {
        customValidator: (value: any) => {
          if (isComplianceMode && !value) {
            return { isValid: false, error: t('Select a compliance playbook') };
          }
          return { isValid: true };
        },
      }),
      new FormField('columns', 'text', !isComplianceMode, t('At least one column'), {
        customValidator: (value: any) => {
          if (isComplianceMode) return { isValid: true };
          if (!value || !Array.isArray(value) || value.length === 0) {
            return { isValid: false, error: t('Define at least one column or select a template') };
          }
          for (let i = 0; i < value.length; i++) {
            if (!value[i].name?.trim()) {
              return { isValid: false, error: t('Column {{n}} needs a name', { n: i + 1 }) };
            }
            if (!value[i].query?.trim()) {
              return { isValid: false, error: t('Column {{n}} needs a query', { n: i + 1 }) };
            }
          }
          return { isValid: true };
        },
      }),
      new FormField('files', 'text', true, t('At least one document'), {
        customValidator: (value: any) => {
          if (!value || !Array.isArray(value) || value.length === 0) {
            return { isValid: false, error: t('Upload at least one document') };
          }
          return { isValid: true };
        },
      }),
      new FormField('collaborators', 'text', false, t('Access')),
    ]);
  }, [t, tableMode]);

  // ─── Submit ─────────────────────────────────────────────────────────────

  const handleFormSubmit = useCallback(
    async (formData: Record<string, any>) => {
      setIsCreating(true);
      sseStartedAtRef.current = Date.now();

      const files: File[] = formData.files || [];
      const isComplianceMode = tableMode === 'compliance';

      const formColumns: ColumnItem[] = formData.columns || [];

      // In compliance mode: backend auto-generates columns from the playbook's clause rules.
      // We only need to tell it which clauses the user removed (by clause uuid).
      // In extraction mode: we send the freeform columns as-is.
      const excludedClauseUuids = isComplianceMode
        ? playbookClauseColumns
            .map((c) => c.clause_uuid)
            .filter((uuid): uuid is string => !!uuid && !formColumns.some((fc) => fc.clause_uuid === uuid))
        : [];

      const extractionColumns = isComplianceMode
        ? []
        : formColumns
            .map((c, i) => ({
              name: c.name.trim(),
              query: c.query.trim(),
              data_type: c.data_type,
              sort_order: i,
            }))
            .filter((c: any) => c.name && c.query);

      const payload: CreateTablePayload = {
        name: String(formData.tableName).trim(),
        description: formData.description?.trim() || undefined,
        mode: tableMode,
        ...(matterId ? { matter_id: matterId, matter_uuid: matterUuid } : {}),
        ...(!isComplianceMode && selectedTemplateId ? { template_id: Number(selectedTemplateId) } : {}),
        ...(isComplianceMode && selectedPlaybookUuid ? { playbook_uuid: selectedPlaybookUuid } : {}),
        ...(isComplianceMode && excludedClauseUuids.length > 0 ? { excluded_clause_rule_uuids: excludedClauseUuids } : {}),
        ...(formData.project_uuid ? { project_uuid: formData.project_uuid } : {}),
        ...(extractionColumns.length > 0 ? { columns: extractionColumns } : {}),
        shared_with: (formData.collaborators || []).map((c: Collaborator) => ({
          id: c.user_id,
          collaborator_uuid: c.collaborator_uuid,
          access: c.access || 'view',
        })),
        ...(isComplianceMode ? { inherit_collaborators: inheritCollaborators } : {}),
      };

      try {
        const result = await dispatch(createBatchTableSSE(payload, files) as any);
        // SSE completed - navigate to the new table
        if (!userNavigatedAwayRef.current) {
          const tableUuid = result?.table?.uuid;
          dispatch(resetBatchTableCreation() as any);
          onClose(tableUuid);
        }
      } catch (error: any) {
        console.error('[CreateTableForm] SSE error:', error);
        // Even on error, if we have a table UUID, navigate to it
        if (error?.table?.uuid && !userNavigatedAwayRef.current) {
          setTimeout(() => {
            dispatch(resetBatchTableCreation() as any);
            onClose(error.table.uuid);
          }, 2000);
        } else {
          setIsCreating(false);
        }
      }
    },
    [dispatch, matterId, matterUuid, onClose, selectedTemplateId, templateColumns, tableMode, selectedPlaybookUuid, playbookClauseColumns, inheritCollaborators]
  );

  // ─── Initial values ────────────────────────────────────────────────────

  const initialValues = useMemo(
    () => ({
      tableName: '',
      description: '',
      template_id: '',
      mode: 'extraction' as TableMode,
      playbook_uuid: '',
      columns: [] as ColumnItem[],
      files: [] as File[],
      collaborators: [] as Collaborator[],
      project_uuid: '',
    }),
    []
  );

  // ─── Cancel SSE ──────────────────────────────────────────────────────────

  const handleCancelCreation = useCallback(() => {
    userNavigatedAwayRef.current = true;
    dispatch(resetBatchTableCreation() as any);
    setIsCreating(false);
    onClose();
  }, [dispatch, onClose]);

  // ─── Render ─────────────────────────────────────────────────────────────

  if (isLoadingData) {
    return <Skeleton variant="form" rows={6} />;
  }

  // Show SSE progress view during creation (like StructureForm / CreateMatter)
  if (isCreating || isConnecting) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center bg-bg">
        <SSECreationProgress
          title={t('Creating table')}
          isConnecting={isConnecting}
          steps={sseSteps}
          connected={connected}
          complete={complete}
          ok={ok}
          summary={summary ?? undefined}
          startedAt={sseStartedAtRef.current ?? undefined}
          onCancel={handleCancelCreation}
        />
      </div>
    );
  }

  return (
    <>
      <BaseForm
        title={t('New table')}
        description={t('Create a table to analyze batches of documents with AI-powered queries.')}
        sections={formSections}
        schema={schema}
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        onCancel={() => onClose()}
        submitLabel={t('Create table')}
        cancelLabel={t('Cancel')}
        isSubmitting={isCreating}
        maxWidth="max-w-4xl"
      />
      <FeatureTour
        steps={createTableFormTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </>
  );
};

export default CreateTableForm;
